import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import SelectController from '@/common/components/SelectController'
import { useGeosOptions } from '@/common/hooks/queries/useGeosOptions'
import useDebounce from '@/common/hooks/useDebounce'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { filterOptions } from '@/features/users/constants/filterOptions.constant'
import { XIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { FilterOptionOption } from 'react-select'
import {
  PublicCompanyLeadsFunnelContractProductCategoryValidationSchema,
  PublicCompanyLeadsFunnelUpdateContractProductPriceValidationSchema,
} from '../schemas/update.schema'

interface ContractProductFixedPriceItemProps {
  productPrices: PublicCompanyLeadsFunnelUpdateContractProductPriceValidationSchema[]
  productPrice: PublicCompanyLeadsFunnelUpdateContractProductPriceValidationSchema
  productIndex: number
  index: number
  onDelete: () => void
  disabled?: boolean
}

const ContractProductFixedPriceItem = ({
  productPrices,
  productPrice: { geo },
  productIndex,
  index,
  onDelete,
  disabled,
}: ContractProductFixedPriceItemProps) => {
  const {
    control,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema>()

  const [geoSearch, setGeoSearch] = useDebounce('')

  const geos = useGeosOptions(geoSearch)

  const watchProductPriceGeo = watch(
    `contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.geo`,
  )

  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="outline-error"
        className="size-[3.25rem] mt-5"
        name={`contract-product-${productIndex}-price_rule_delete-${index}`}
        onClick={onDelete}
        disabled={disabled}
      >
        <XIcon strokeWidth={1} />
      </Button>

      <SelectController
        control={control}
        name={`contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.geo`}
        dataTestid={`contract-product-${productIndex}-price_rule_geo-${index}`}
        label="Região de atuação"
        className="w-1/2"
        onInputChange={setGeoSearch}
        filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
        options={
          GeoTransformers.mapToGeoOptions()(geos)?.filter(
            (option) =>
              !productPrices?.some(
                (productPrice) => productPrice?.geo?.value === option.value && productPrice?.geo?.type === option.type,
              ),
          ) || []
        }
        onChange={(value) => {
          if (value) {
            setValue(
              `contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.geo`,
              value,
            )
            clearErrors(
              `contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.geo`,
            )
          }
        }}
        isDisabled={!!geo || disabled}
        errorMessage={
          errors?.contract_product_category_products?.[productIndex]?.contract_product_category_product_prices?.[index]
            ?.geo?.message
        }
      />

      <Controller
        control={control}
        name={`contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.value`}
        render={({ field }) => (
          <CurrencyTextField
            dataTestid={`contract-product-${productIndex}-price_rule_value-${index}`}
            label="Valor a ser praticado"
            className="w-1/2"
            {...field}
            errorMessage={
              errors?.contract_product_category_products?.[productIndex]?.contract_product_category_product_prices?.[
                index
              ]?.value?.message
            }
            disabled={disabled || !watchProductPriceGeo}
          />
        )}
      />
    </div>
  )
}

export default ContractProductFixedPriceItem
